import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  limit,
  query,
  orderBy,
  startAfter,
  where,
  documentId,
} from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { FaSpinner, FaIdBadge, FaGlobe, FaBuilding, FaTimes, FaMobileAlt, FaDesktop, FaComments, FaRobot } from 'react-icons/fa';
import UserActivityReviewer from './UserActivityReviewer';

const thClass = 'whitespace-nowrap border-b border-slate-200 px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500';
const tdClass = 'border-b border-slate-100 px-4 py-2.5 align-top text-[13px] text-slate-800';

/** Firestore `in` supports at most 30 values. */
const FIRESTORE_IN_MAX = 30;

function pickUserLabel(data, uid) {
  if (!data) return uid || 'Unknown';
  const combined = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return (
    data.name ||
    data.displayName ||
    data.userName ||
    data.email ||
    data.companyName ||
    uid ||
    'Unknown'
  );
}

async function fetchUsersByIds(userIds) {
  const out = {};
  const ids = [...new Set(userIds.filter(Boolean))];
  for (let i = 0; i < ids.length; i += FIRESTORE_IN_MAX) {
    const chunk = ids.slice(i, i + FIRESTORE_IN_MAX);
    const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
    snap.forEach((d) => {
      out[d.id] = d.data();
    });
  }
  return out;
}

/** Web logs `platform: 'web'` + userAgent; iOS logs omit those fields. */
function resolvePlatform(record) {
  const d = record?.details || {};
  const raw = [
    record?.platform,
    d.platform,
    record?.source,
    d.source,
    record?.client,
    d.client,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase())
    .join(' ');

  if (/\b(ios|android|mobile|iphone|ipad|native|app)\b/.test(raw)) return 'mobile';
  if (raw === 'web' || /\b(web|online|browser|desktop)\b/.test(raw)) return 'web';
  if (record?.userAgent || d.userAgent) return 'web';
  if (record?.referrer != null || record?.screenResolution || d.referrer != null) return 'web';
  if (record?.userId || record?.action) return 'mobile';
  return 'unknown';
}

function formatActivityVisit(record) {
  const action = record.action || record.details?.action || 'unknown';
  const page = record.page || record.details?.page;
  const signId = record.signId || record.details?.signId;
  const query = record.query || record.details?.query;
  const campaignId = record.campaignId || record.details?.campaignId;

  if (action === 'sign_view') {
    const base = signId ? `Viewed sign ${signId}` : 'Sign view';
    return page ? `${base} (${page})` : base;
  }
  if (action === 'page_view') return page ? `Visited ${page}` : 'Page view';
  if (action === 'search') return query ? `Search: "${query}"` : 'Search';
  const parts = [action.replace(/_/g, ' ')];
  if (page) parts.push(`on ${page}`);
  if (signId) parts.push(`sign ${signId}`);
  if (query) parts.push(`"${query}"`);
  if (campaignId) parts.push(`campaign ${campaignId}`);
  return parts.join(' · ');
}

const PlatformBadge = ({ record, className = '' }) => {
  const kind = resolvePlatform(record);
  if (kind === 'web') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800 ${className}`}
        title="Web (online)"
      >
        <FaDesktop className="shrink-0 text-[10px]" aria-hidden />
        Web
      </span>
    );
  }
  if (kind === 'mobile') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800 ${className}`}
        title="Mobile app"
      >
        <FaMobileAlt className="shrink-0 text-[10px]" aria-hidden />
        App
      </span>
    );
  }
  return <span className={`text-xs text-slate-400 ${className}`}>—</span>;
};

const ParamChips = ({ obj }) => {
  const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!entries.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => {
        const display = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return (
          <span
            key={k}
            className="rounded px-1.5 py-0.5 font-mono text-[11px] leading-relaxed bg-slate-100 text-slate-800"
          >
            <span className="text-slate-500">{k}:</span>{' '}
            <span className="font-semibold text-primary">{display.length > 60 ? display.slice(0, 60) + '…' : display}</span>
          </span>
        );
      })}
    </div>
  );
};

const FirebaseEventsTab = ({ allActivity, userMap, loading, dateRange, setDateRange, fetchAnalytics, onUserClick }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('count');

  const resolveUser = (uid) => {
    if (!uid) return <span className="text-slate-400">—</span>;
    const u = userMap[uid];
    const name = pickUserLabel(u, uid);
    return (
      <button
        type="button"
        className="text-left font-medium text-primary hover:underline"
        title={uid}
        onClick={(e) => {
          e.stopPropagation();
          onUserClick?.(uid);
        }}
      >
        {name}
      </button>
    );
  };

  const eventGroups = useMemo(() => {
    const groups = {};
    allActivity.forEach(a => {
      const action = a.action || 'unknown';
      if (!groups[action]) groups[action] = { action, count: 0, lastSeen: null, uniqueUsers: new Set(), recent: [] };
      groups[action].count++;
      if (!groups[action].lastSeen || a.timestamp > groups[action].lastSeen) groups[action].lastSeen = a.timestamp;
      if (a.userId) groups[action].uniqueUsers.add(a.userId);
      groups[action].recent.push(a);
    });
    return Object.values(groups).map(g => ({ ...g, uniqueUsers: g.uniqueUsers.size }));
  }, [allActivity]);

  const sorted = useMemo(() => {
    const filtered = search
      ? eventGroups.filter(g => g.action.toLowerCase().includes(search.toLowerCase()))
      : eventGroups;
    return [...filtered].sort((a, b) =>
      sortBy === 'count' ? b.count - a.count :
      sortBy === 'users' ? b.uniqueUsers - a.uniqueUsers :
      (b.lastSeen || 0) - (a.lastSeen || 0)
    );
  }, [eventGroups, search, sortBy]);

  const selectedGroup = selectedEvent ? eventGroups.find(g => g.action === selectedEvent) : null;

  const SortBtn = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setSortBy(id)}
      className={sortBy === id ? 'f-button !min-h-8 !px-3 !py-1.5 !text-xs' : 'f-button f-button-secondary !min-h-8 !px-3 !py-1.5 !text-xs'}
    >
      {label}
    </button>
  );

  const totalEvents = eventGroups.reduce((s, g) => s + g.count, 0);
  const totalUnique = new Set(allActivity.map(a => a.userId).filter(Boolean)).size;

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          placeholder="Filter by event name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="f-input min-w-[200px] max-w-sm flex-1"
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Sort</span>
          <SortBtn id="count" label="Count" />
          <SortBtn id="users" label="Users" />
          <SortBtn id="recent" label="Recent" />
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="f-input w-[120px]"
        >
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
        </select>
        <button type="button" className="f-button" onClick={fetchAnalytics}>
          Refresh
        </button>
      </div>

      {!loading && (
        <div className="f-grid">
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Total Events</span>
            <span className="f-stat-value">{totalEvents.toLocaleString()}</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Unique Event Types</span>
            <span className="f-stat-value text-primary">{eventGroups.length}</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Unique Users</span>
            <span className="f-stat-value text-emerald-600">{totalUnique}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-slate-200/80 bg-white/90 p-10 shadow-sm">
          <FaSpinner className="fa-spin text-3xl text-primary" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Loading events…</p>
        </div>
      ) : (
        <>
          <div className="f-card overflow-hidden p-0">
            <div className="border-b border-slate-100 px-5 py-3.5 text-sm font-semibold text-slate-900">
              {sorted.length} event type{sorted.length !== 1 ? 's' : ''} — click a row to see recent occurrences
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Event Name</th>
                    <th className={`${thClass} text-right`}>Count</th>
                    <th className={`${thClass} text-right`}>Unique Users</th>
                    <th className={`${thClass} text-right`}>Last Seen</th>
                    <th className={`${thClass} w-6`} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((g) => (
                    <tr
                      key={g.action}
                      onClick={() => setSelectedEvent(selectedEvent === g.action ? null : g.action)}
                      className={`cursor-pointer transition-colors hover:bg-slate-50/80 ${
                        selectedEvent === g.action ? 'bg-sky-50/80' : ''
                      }`}
                    >
                      <td className={tdClass}>
                        <code className="font-mono text-xs font-bold text-primary">{g.action}</code>
                      </td>
                      <td className={`${tdClass} text-right text-base font-bold`}>{g.count.toLocaleString()}</td>
                      <td className={`${tdClass} text-right`}>{g.uniqueUsers}</td>
                      <td className={`${tdClass} text-right text-xs text-slate-500`}>
                        {g.lastSeen ? dayjs(g.lastSeen).format('MMM D, h:mm A') : '—'}
                      </td>
                      <td className={`${tdClass} text-xs text-slate-400`}>{selectedEvent === g.action ? '▲' : '▼'}</td>
                    </tr>
                  ))}
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">
                        No events found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {selectedGroup && (
            <div className="f-card overflow-hidden p-0">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="rounded-md bg-sky-100 px-2 py-1 font-mono text-xs font-bold text-primary">
                    {selectedGroup.action}
                  </code>
                  <span className="text-sm font-semibold text-slate-900">Recent occurrences</span>
                </div>
                <span className="text-xs text-slate-500">
                  Showing {Math.min(100, selectedGroup.recent.length)} of {selectedGroup.count.toLocaleString()} total
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Time</th>
                      <th className={thClass}>Platform</th>
                      <th className={thClass}>User</th>
                      <th className={thClass}>Visit / Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...selectedGroup.recent]
                      .sort((a, b) => b.timestamp - a.timestamp)
                      .slice(0, 100)
                      .map((ev, i) => {
                        const raw = ev.details || {};
                        const extra = {};
                        if (ev.signId) extra.signId = ev.signId;
                        if (ev.page) extra.page = ev.page;
                        if (ev.query) extra.query = ev.query;
                        if (ev.campaignId) extra.campaignId = ev.campaignId;
                        const params = { ...extra, ...raw };
                        delete params.action;
                        delete params.userId;
                        delete params.timestamp;
                        return (
                          <tr key={i}>
                            <td className={`${tdClass} whitespace-nowrap text-xs text-slate-500`}>
                              {dayjs(ev.timestamp).format('MMM D, h:mm A')}
                            </td>
                            <td className={tdClass}>
                              <PlatformBadge record={ev} />
                            </td>
                            <td className={`${tdClass} whitespace-nowrap font-medium`}>{resolveUser(ev.userId)}</td>
                            <td className={tdClass}>
                              <div className="mb-1 text-[13px] font-medium text-slate-900">
                                {formatActivityVisit(ev)}
                              </div>
                              <ParamChips obj={params} />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────

function sortBillMessages(messages) {
  return [...messages].sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    if (ta !== tb) return ta - tb;
    if (a.role !== b.role) return a.role === 'user' ? -1 : 1;
    return String(a.id).localeCompare(String(b.id));
  });
}

function mapFirestoreTs(ts) {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate();
  if (typeof ts === 'string') return new Date(ts);
  return new Date(ts);
}

const AiAssistantTab = ({ onUserClick }) => {
  const [dateRange, setDateRange] = useState(7);
  const [loading, setLoading] = useState(true);
  const [invocations, setInvocations] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [selectedSession, setSelectedSession] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  const fetchAiAssistantData = async () => {
    setLoading(true);
    setError(null);
    try {
      const startDate = dayjs().subtract(dateRange, 'day').toDate();
      const pageSize = 500;
      const maxDocs = 10000;

      const auditDocs = [];
      let lastAudit = null;
      while (true) {
        const auditQuery = lastAudit
          ? query(
              collection(db, 'assistant_audit'),
              where('createdAt', '>=', startDate),
              orderBy('createdAt', 'desc'),
              startAfter(lastAudit),
              limit(pageSize)
            )
          : query(
              collection(db, 'assistant_audit'),
              where('createdAt', '>=', startDate),
              orderBy('createdAt', 'desc'),
              limit(pageSize)
            );
        const snap = await getDocs(auditQuery);
        if (snap.empty) break;
        snap.docs.forEach((d) => {
          const data = d.data();
          auditDocs.push({
            id: d.id,
            uid: data.uid,
            sessionId: data.sessionId,
            userMessage: data.userMessage || '',
            assistantText: data.assistantText || '',
            toolCalls: Array.isArray(data.toolCalls) ? data.toolCalls : [],
            usage: data.usage || {},
            latencyMs: Number(data.latencyMs || 0),
            error: data.error,
            model: data.model,
            createdAt: mapFirestoreTs(data.createdAt),
          });
        });
        lastAudit = snap.docs[snap.docs.length - 1];
        if (snap.size < pageSize || auditDocs.length >= maxDocs) break;
      }

      const convDocs = [];
      let lastConv = null;
      while (true) {
        const convQuery = lastConv
          ? query(
              collection(db, 'assistant_conversations'),
              where('updatedAt', '>=', startDate),
              orderBy('updatedAt', 'desc'),
              startAfter(lastConv),
              limit(pageSize)
            )
          : query(
              collection(db, 'assistant_conversations'),
              where('updatedAt', '>=', startDate),
              orderBy('updatedAt', 'desc'),
              limit(pageSize)
            );
        const snap = await getDocs(convQuery);
        if (snap.empty) break;
        snap.docs.forEach((d) => {
          const data = d.data();
          convDocs.push({
            sessionId: String(data.sessionId || d.id),
            uid: data.uid,
            title: String(data.title || 'Conversation'),
            preview: String(data.preview || ''),
            messageCount: Number(data.messageCount || 0),
            createdAt: mapFirestoreTs(data.createdAt),
            updatedAt: mapFirestoreTs(data.updatedAt),
          });
        });
        lastConv = snap.docs[snap.docs.length - 1];
        if (snap.size < pageSize || convDocs.length >= maxDocs) break;
      }

      const turnsBySession = {};
      auditDocs.forEach((a) => {
        if (!a.sessionId) return;
        turnsBySession[a.sessionId] = (turnsBySession[a.sessionId] || 0) + 1;
      });

      const convs = convDocs.map((c) => ({
        ...c,
        turnCount: turnsBySession[c.sessionId] || 0,
      }));

      const uids = [
        ...new Set([
          ...auditDocs.map((a) => a.uid),
          ...convs.map((c) => c.uid),
        ].filter(Boolean)),
      ];
      const uMap = await fetchUsersByIds(uids);

      setInvocations(auditDocs);
      setConversations(convs);
      setUserMap(uMap);
      if (selectedSession && !convs.some((c) => c.sessionId === selectedSession.sessionId)) {
        setSelectedSession(null);
        setThreadMessages([]);
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load AI assistant data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAiAssistantData();
  }, [dateRange]);

  const loadThread = async (conv) => {
    setSelectedSession(conv);
    setThreadLoading(true);
    setThreadMessages([]);
    try {
      const snap = await getDocs(
        query(
          collection(db, 'assistant_conversations', conv.sessionId, 'messages'),
          orderBy('createdAt', 'asc')
        )
      );
      const messages = snap.docs.map((d) => {
        const m = d.data();
        return {
          id: d.id,
          role: m.role === 'user' ? 'user' : 'assistant',
          content: String(m.content || ''),
          signs: Array.isArray(m.signs) ? m.signs : undefined,
          plan: m.plan && typeof m.plan === 'object' ? m.plan : undefined,
          createdAt: mapFirestoreTs(m.createdAt)?.toISOString?.() || new Date().toISOString(),
        };
      });
      setThreadMessages(sortBillMessages(messages));
    } catch (e) {
      console.error(e);
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  };

  const invocationsByDay = useMemo(() => {
    const byDay = {};
    invocations.forEach((inv) => {
      if (!inv.createdAt) return;
      const ds = dayjs(inv.createdAt).format('YYYY-MM-DD');
      byDay[ds] = (byDay[ds] || 0) + 1;
    });
    return Object.entries(byDay)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [invocations]);

  const sessionInvocations = useMemo(() => {
    if (!selectedSession) return [];
    return invocations
      .filter((inv) => inv.sessionId === selectedSession.sessionId)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [invocations, selectedSession]);

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => {
      const name = pickUserLabel(userMap[c.uid], c.uid).toLowerCase();
      return (
        name.includes(q) ||
        c.title.toLowerCase().includes(q) ||
        c.preview.toLowerCase().includes(q) ||
        c.sessionId.toLowerCase().includes(q)
      );
    });
  }, [conversations, search, userMap]);

  const totalTokens = useMemo(() => {
    return invocations.reduce(
      (acc, inv) => {
        acc.input += Number(inv.usage?.inputTokens || 0);
        acc.output += Number(inv.usage?.outputTokens || 0);
        return acc;
      },
      { input: 0, output: 0 }
    );
  }, [invocations]);

  const avgLatency = useMemo(() => {
    if (!invocations.length) return 0;
    const sum = invocations.reduce((s, inv) => s + (inv.latencyMs || 0), 0);
    return Math.round(sum / invocations.length);
  }, [invocations]);

  const uniqueUsers = useMemo(() => new Set(invocations.map((i) => i.uid).filter(Boolean)).size, [invocations]);
  const errorCount = useMemo(() => invocations.filter((i) => i.error).length, [invocations]);

  const resolveUser = (uid) => {
    if (!uid) return <span className="text-slate-400">—</span>;
    const name = pickUserLabel(userMap[uid], uid);
    return (
      <button
        type="button"
        className="text-left font-medium text-primary hover:underline"
        title={uid}
        onClick={(e) => {
          e.stopPropagation();
          onUserClick?.(uid);
        }}
      >
        {name}
      </button>
    );
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          placeholder="Filter conversations by user, title, or session…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="f-input min-w-[200px] max-w-sm flex-1"
        />
        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="f-input w-[120px]"
        >
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
        </select>
        <button type="button" className="f-button" onClick={fetchAiAssistantData}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
          <span className="mt-1 block text-xs text-red-600">
            Ensure your user has <code>admin: true</code> and Firestore rules allow admin reads on assistant collections.
          </span>
        </div>
      )}

      {!loading && (
        <div className="f-grid">
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Invocations</span>
            <span className="f-stat-value">{invocations.length.toLocaleString()}</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Conversations</span>
            <span className="f-stat-value text-primary">{conversations.length.toLocaleString()}</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Unique Users</span>
            <span className="f-stat-value text-emerald-600">{uniqueUsers}</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Avg Latency</span>
            <span className="f-stat-value">{(avgLatency / 1000).toFixed(1)}s</span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Tokens In / Out</span>
            <span className="f-stat-value text-sm">
              {(totalTokens.input / 1000).toFixed(1)}k / {(totalTokens.output / 1000).toFixed(1)}k
            </span>
          </div>
          <div className="f-card f-stat-card">
            <span className="f-stat-label">Errors</span>
            <span className={`f-stat-value ${errorCount ? 'text-red-600' : ''}`}>{errorCount}</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-slate-200/80 bg-white/90 p-10 shadow-sm">
          <FaSpinner className="fa-spin text-3xl text-primary" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Loading Bill (AI assistant) metrics…</p>
        </div>
      ) : (
        <>
          <div className="f-card">
            <div className="mb-3 text-sm font-semibold text-slate-900">Invocations by day</div>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={invocationsByDay}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(280px,38%)_1fr]">
            <div className="f-card overflow-hidden p-0">
              <div className="border-b border-slate-100 px-5 py-3.5 text-sm font-semibold text-slate-900">
                {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
              </div>
              <div className="max-h-[520px] overflow-y-auto">
                {filteredConversations.length === 0 ? (
                  <p className="px-5 py-10 text-center text-sm text-slate-500">No conversations in range</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredConversations.map((c) => {
                      const active = selectedSession?.sessionId === c.sessionId;
                      return (
                        <li key={c.sessionId}>
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => loadThread(c)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                loadThread(c);
                              }
                            }}
                            className={`w-full cursor-pointer px-4 py-3 text-left transition-colors hover:bg-slate-50/80 ${
                              active ? 'bg-violet-50/80' : ''
                            }`}
                          >
                            <div className="mb-1 flex items-start justify-between gap-2">
                              <span className="line-clamp-1 text-[13px] font-semibold text-slate-900">{c.title}</span>
                              <span className="shrink-0 text-[11px] text-slate-400">
                                {c.updatedAt ? dayjs(c.updatedAt).format('MMM D') : '—'}
                              </span>
                            </div>
                            <div className="mb-1 text-xs font-medium">{resolveUser(c.uid)}</div>
                            <p className="line-clamp-2 text-xs text-slate-500">{c.preview || '—'}</p>
                            <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-slate-400">
                              <span>{c.turnCount || 0} turn{(c.turnCount || 0) !== 1 ? 's' : ''}</span>
                              <span>·</span>
                              <span>{c.messageCount} msg{c.messageCount !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="f-card flex min-h-[400px] flex-col overflow-hidden p-0">
              {!selectedSession ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
                  <FaComments className="text-4xl text-slate-300" aria-hidden />
                  <p className="text-sm text-slate-500">Select a conversation to browse the thread</p>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-3.5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">{selectedSession.title}</h3>
                        <div className="mt-1 text-xs text-slate-500">
                          {resolveUser(selectedSession.uid)}
                          <span className="mx-2">·</span>
                          <code className="font-mono text-[11px]">{selectedSession.sessionId}</code>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-slate-500 hover:text-slate-800"
                        onClick={() => {
                          setSelectedSession(null);
                          setThreadMessages([]);
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {threadLoading ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10">
                      <FaSpinner className="fa-spin text-2xl text-primary" aria-hidden />
                      <span className="text-sm text-slate-600">Loading messages…</span>
                    </div>
                  ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                      <div className="space-y-3">
                        {threadMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                                msg.role === 'user'
                                  ? 'bg-primary text-white'
                                  : 'border border-slate-200 bg-white text-slate-800'
                              }`}
                            >
                              {msg.role === 'assistant' && (
                                <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-violet-600">
                                  <FaRobot className="text-[9px]" aria-hidden />
                                  Bill
                                </div>
                              )}
                              <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                              {msg.signs?.length > 0 && (
                                <div className="mt-2 text-[11px] opacity-80">
                                  {msg.signs.length} sign{msg.signs.length !== 1 ? 's' : ''} shown
                                </div>
                              )}
                              {msg.plan?.name && (
                                <div className="mt-2 rounded bg-violet-50 px-2 py-1 text-[11px] text-violet-800">
                                  Campaign plan: {msg.plan.name}
                                </div>
                              )}
                              <div
                                className={`mt-1 text-[10px] ${
                                  msg.role === 'user' ? 'text-white/70' : 'text-slate-400'
                                }`}
                              >
                                {dayjs(msg.createdAt).format('MMM D, h:mm A')}
                              </div>
                            </div>
                          </div>
                        ))}
                        {threadMessages.length === 0 && (
                          <p className="py-8 text-center text-sm text-slate-500">No messages in this thread</p>
                        )}
                      </div>

                      {sessionInvocations.length > 0 && (
                        <div className="mt-6 border-t border-slate-100 pt-4">
                          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Turn audit ({sessionInvocations.length})
                          </h4>
                          <div className="space-y-2">
                            {sessionInvocations.slice(0, 20).map((inv) => (
                              <details
                                key={inv.id}
                                className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs"
                              >
                                <summary className="cursor-pointer font-medium text-slate-700">
                                  {inv.createdAt ? dayjs(inv.createdAt).format('MMM D, h:mm A') : '—'}
                                  <span className="ml-2 text-slate-400">
                                    {(inv.latencyMs / 1000).toFixed(1)}s
                                    {inv.toolCalls?.length ? ` · ${inv.toolCalls.length} tool${inv.toolCalls.length !== 1 ? 's' : ''}` : ''}
                                    {inv.error ? ' · error' : ''}
                                  </span>
                                </summary>
                                <div className="mt-2 space-y-2 text-slate-600">
                                  <div>
                                    <span className="font-semibold text-slate-700">User:</span>{' '}
                                    <span className="whitespace-pre-wrap">{inv.userMessage.slice(0, 500)}</span>
                                  </div>
                                  <div>
                                    <span className="font-semibold text-slate-700">Assistant:</span>{' '}
                                    <span className="whitespace-pre-wrap">{inv.assistantText.slice(0, 800)}</span>
                                  </div>
                                  {inv.toolCalls?.length > 0 && (
                                    <div>
                                      <span className="font-semibold text-slate-700">Tools:</span>{' '}
                                      {inv.toolCalls.map((t) => t.name).join(', ')}
                                    </div>
                                  )}
                                  {inv.error && (
                                    <div className="text-red-600">
                                      <span className="font-semibold">Error:</span> {inv.error}
                                    </div>
                                  )}
                                </div>
                              </details>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────

const Analytics = () => {
  const [dateRange, setDateRange] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [recentUsers, setRecentUsers] = useState([]);
  const [signViewsByDay, setSignViewsByDay] = useState([]);
  const [pageViewsByDay, setPageViewsByDay] = useState([]);
  const [searchesByDay, setSearchesByDay] = useState([]);
  const [signViewsBySign, setSignViewsBySign] = useState([]);
  const [allActivity, setAllActivity] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [mainChartMetric, setMainChartMetric] = useState('activeUsers');
  const [userModal, setUserModal] = useState({ open: false, user: null, loading: false, activities: [] });

  const chartMetricLabels = { activeUsers: 'Active Users', signViews: 'Sign Views', pageViews: 'Page Views', searches: 'Searches' };
  const chartMetricColors = { activeUsers: '#a78bfa', signViews: '#0b7fd4', pageViews: '#059669', searches: '#d97706' };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const startDate = dayjs().subtract(dateRange, 'day').toDate();
      const activityDocs = [];
      let lastDoc = null;
      const pageSize = 1000;
      const maxDocs = 20000;

      while (true) {
        const activityQuery = lastDoc
          ? query(
              collection(db, 'user_activity'),
              where('timestamp', '>=', startDate),
              orderBy('timestamp', 'desc'),
              startAfter(lastDoc),
              limit(pageSize)
            )
          : query(
              collection(db, 'user_activity'),
              where('timestamp', '>=', startDate),
              orderBy('timestamp', 'desc'),
              limit(pageSize)
            );

        const snap = await getDocs(activityQuery);
        if (snap.empty) break;
        activityDocs.push(...snap.docs);
        lastDoc = snap.docs[snap.docs.length - 1];
        if (snap.size < pageSize || activityDocs.length >= maxDocs) break;
      }
      
      const usersQuery = query(collection(db, 'users'), limit(1000));
      const usersSnap = await getDocs(usersQuery);
      const uMap = {};
      usersSnap.forEach(d => { uMap[d.id] = d.data(); });

      const svByDay = {}, pvByDay = {}, sByDay = {}, svBySign = {}, usersAct = {};
      const allAct = [];

      activityDocs.forEach(activityDoc => {
        const d = activityDoc.data();
        const details = d.details || {};
        const action = d.action || details.action;
        const ts = d.timestamp?.toDate ? d.timestamp.toDate() : (typeof d.timestamp === 'string' ? new Date(d.timestamp) : new Date());
        const ds = dayjs(ts).format('YYYY-MM-DD');
        const uid = d.userId || details.userId;

        const record = { ...d, timestamp: ts, date: ds, action, userId: uid };
        allAct.push(record);

        if (uid && !usersAct[uid]) usersAct[uid] = { userId: uid, lastActive: ts, signViews: 0, pageViews: 0 };
        if (uid && ts > (usersAct[uid]?.lastActive || 0)) usersAct[uid].lastActive = ts;

        if (action === 'sign_view') {
          svByDay[ds] = (svByDay[ds] || 0) + 1;
          const sid = d.signId || details.signId;
          if (sid) svBySign[sid] = (svBySign[sid] || 0) + 1;
          if (uid) usersAct[uid].signViews++;
        } else if (action === 'page_view') {
          pvByDay[ds] = (pvByDay[ds] || 0) + 1;
          if (uid) usersAct[uid].pageViews++;
        } else if (action === 'search') {
          sByDay[ds] = (sByDay[ds] || 0) + 1;
        }
      });

      const activityUserIds = [...new Set(allAct.map((a) => a.userId).filter(Boolean))];
      const missingIds = activityUserIds.filter((id) => !uMap[id]);
      if (missingIds.length) {
        const extra = await fetchUsersByIds(missingIds);
        Object.assign(uMap, extra);
      }
      setUserMap(uMap);

      setAllActivity(allAct);
      setSignViewsByDay(Object.entries(svByDay).map(([date, count]) => ({ date, count })).sort((a,b) => a.date.localeCompare(b.date)));
      setPageViewsByDay(Object.entries(pvByDay).map(([date, count]) => ({ date, count })).sort((a,b) => a.date.localeCompare(b.date)));
      setSearchesByDay(Object.entries(sByDay).map(([date, count]) => ({ date, count })).sort((a,b) => a.date.localeCompare(b.date)));
      setSignViewsBySign(Object.entries(svBySign).map(([signId, count]) => ({ signId, count })).sort((a,b) => b.count - a.count));
      setRecentUsers(Object.values(usersAct).sort((a,b) => b.lastActive - a.lastActive));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const activeUsersByDay = useMemo(() => {
    const usersByDay = {};
    allActivity.forEach(a => {
      if (!a.userId) return;
      const ds = dayjs(a.timestamp).format('YYYY-MM-DD');
      if (!usersByDay[ds]) usersByDay[ds] = new Set();
      usersByDay[ds].add(a.userId);
    });
    return Object.entries(usersByDay)
      .map(([date, users]) => ({ date, count: users.size }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [allActivity]);

  const mainChartData = useMemo(() => {
    if (mainChartMetric === 'activeUsers') return activeUsersByDay;
    if (mainChartMetric === 'signViews') return signViewsByDay;
    if (mainChartMetric === 'pageViews') return pageViewsByDay;
    if (mainChartMetric === 'searches') return searchesByDay;
    return [];
  }, [mainChartMetric, activeUsersByDay, signViewsByDay, pageViewsByDay, searchesByDay]);

  const latestActivityByUser = useMemo(() => {
    const map = {};
    allActivity.forEach((a) => {
      if (!a.userId) return;
      const existing = map[a.userId];
      if (!existing || a.timestamp > existing.timestamp) map[a.userId] = a;
    });
    return map;
  }, [allActivity]);

  const openUserModal = async (uid) => {
    if (!uid) return;
    const activities = allActivity
      .filter((a) => a.userId === uid)
      .sort((a, b) => b.timestamp - a.timestamp);
    setUserModal({ open: true, user: { id: uid }, loading: true, activities });
    try {
      const d = await getDoc(doc(db, 'users', uid));
      setUserModal({
        open: true,
        loading: false,
        activities,
        user: d.exists() ? { ...d.data(), id: uid } : { id: uid },
      });
    } catch (err) {
      console.error(err);
      setUserModal({ open: true, loading: false, user: { id: uid }, activities });
    }
  };

  const timeAgo = (ts) => {
    if (!ts) return '—';
    const mins = Math.round((Date.now() - ts.getTime()) / 60000);
    if (mins < 1) return 'Active';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins/60)}h ago`;
    return `${Math.round(mins/1440)}d ago`;
  };

  const resolveUserName = (uid) => pickUserLabel(userMap[uid], uid);

  const tabs = [
    { id: 'overview', label: 'Dashboard' },
    { id: 'user-activity', label: 'Activity (Postgres)' },
    { id: 'ai-assistant', label: 'AI Assistant' },
    { id: 'firebase-events', label: 'Firebase Events' },
  ];

  const modalActivities = userModal.activities || [];
  const modalWebCount = modalActivities.filter((a) => resolvePlatform(a) === 'web').length;
  const modalMobileCount = modalActivities.filter((a) => resolvePlatform(a) === 'mobile').length;

  const modalEl =
    userModal.open &&
    userModal.user &&
    createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="analytics-user-modal-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) setUserModal({ open: false, user: null, loading: false, activities: [] });
        }}
      >
        <div
          className="f-card flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden p-0 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 id="analytics-user-modal-title" className="m-0 text-lg font-semibold text-slate-900">
              User audit
            </h2>
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
              onClick={() => setUserModal({ open: false, user: null, loading: false, activities: [] })}
              aria-label="Close"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            {userModal.loading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10">
                <FaSpinner className="fa-spin text-2xl text-primary" aria-hidden />
                <span className="text-sm text-slate-600">Loading profile…</span>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-white">
                    {(pickUserLabel(userModal.user, userModal.user.id)[0] || '?').toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">
                      {pickUserLabel(userModal.user, userModal.user.id) !== userModal.user.id
                        ? pickUserLabel(userModal.user, userModal.user.id)
                        : userModal.user.id}
                    </div>
                    <div className="text-sm text-slate-500">{userModal.user.email || '—'}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-3 text-sm text-slate-700">
                  <div className="flex items-start gap-2">
                    <FaIdBadge className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
                    <span>
                      <span className="font-semibold text-slate-900">ID:</span> {userModal.user.id}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FaBuilding className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
                    <span>
                      <span className="font-semibold text-slate-900">Company:</span>{' '}
                      {userModal.user.companyName || '—'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <FaGlobe className="mt-0.5 shrink-0 text-slate-400" aria-hidden />
                    <span>
                      <span className="font-semibold text-slate-900">Website:</span> {userModal.user.website || '—'}
                    </span>
                  </div>
                </div>

                <div className="mb-4 flex flex-wrap gap-2">
                  <span className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                    {modalActivities.length} event{modalActivities.length !== 1 ? 's' : ''} in range
                  </span>
                  <span className="rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-800">
                    {modalWebCount} web
                  </span>
                  <span className="rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-800">
                    {modalMobileCount} mobile app
                  </span>
                </div>

                <h3 className="mb-3 text-sm font-semibold text-slate-900">Activity trail</h3>
                {modalActivities.length === 0 ? (
                  <p className="text-sm text-slate-500">No activity recorded for this user in the selected date range.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className={thClass}>Time</th>
                          <th className={thClass}>Platform</th>
                          <th className={thClass}>Visit / action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalActivities.slice(0, 200).map((ev, i) => (
                          <tr key={i} className="hover:bg-slate-50/80">
                            <td className={`${tdClass} whitespace-nowrap text-xs text-slate-500`}>
                              {dayjs(ev.timestamp).format('MMM D, h:mm A')}
                            </td>
                            <td className={tdClass}>
                              <PlatformBadge record={ev} />
                            </td>
                            <td className={tdClass}>
                              <div className="font-medium text-slate-900">{formatActivityVisit(ev)}</div>
                              {(ev.signId || ev.page || ev.details?.query) && (
                                <div className="mt-1 text-xs text-slate-500">
                                  {ev.page && <span className="mr-2">page: {ev.page}</span>}
                                  {ev.signId && <span className="mr-2">sign: {ev.signId}</span>}
                                  {(ev.query || ev.details?.query) && (
                                    <span>query: {ev.query || ev.details?.query}</span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {modalActivities.length > 200 && (
                      <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                        Showing 200 of {modalActivities.length} events
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
    <div className="f-page-enter mx-auto max-w-[1920px] px-4 pb-8 md:px-6">
      <div className="f-header mb-6 flex flex-col gap-4 rounded-xl border border-slate-200/80 shadow-sm md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="f-title">Analytics</h1>
          <p className="f-subtitle mb-0">
            {activeTab === 'overview'
              ? `Last ${dateRange} days activity overview`
              : activeTab === 'user-activity'
                ? 'Postgres user-activity-service — growth, active users, per-user timeline'
                : activeTab === 'ai-assistant'
                  ? 'Bill (AI assistant) — invocations, conversations, and thread browser'
                  : 'Firebase Analytics – events and parameters'}
          </p>
        </div>
        {activeTab === 'overview' && (
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(Number(e.target.value))}
              className="f-input w-[120px]"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
            </select>
            <button type="button" className="f-button" onClick={fetchAnalytics}>
              Refresh
            </button>
          </div>
        )}
      </div>

      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={[
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === t.id
                ? 'border-primary font-semibold text-primary'
                : 'border-transparent text-slate-500 hover:text-slate-800',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'user-activity' && <UserActivityReviewer />}

      {activeTab === 'ai-assistant' && (
        <AiAssistantTab onUserClick={openUserModal} />
      )}

      {activeTab === 'firebase-events' && (
        <FirebaseEventsTab
          allActivity={allActivity}
          userMap={userMap}
          loading={loading}
          dateRange={dateRange}
          setDateRange={setDateRange}
          fetchAnalytics={fetchAnalytics}
          onUserClick={openUserModal}
        />
      )}

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="f-grid">
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Active Users</span>
              <span className="f-stat-value">{recentUsers.length}</span>
            </div>
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Sign Views</span>
              <span className="f-stat-value text-primary">
                {signViewsByDay.reduce((s, d) => s + d.count, 0)}
              </span>
            </div>
            <div className="f-card f-stat-card">
              <span className="f-stat-label">Page Views</span>
              <span className="f-stat-value text-emerald-600">
                {allActivity.filter((d) => d.action === 'page_view').length}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-1 border-b border-slate-200">
            {Object.keys(chartMetricLabels).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMainChartMetric(m)}
                className={[
                  '-mb-px border-b-2 px-4 py-2 text-sm transition-colors',
                  mainChartMetric === m
                    ? 'border-primary font-semibold text-primary'
                    : 'border-transparent font-normal text-slate-500 hover:text-slate-800',
                ].join(' ')}
              >
                {chartMetricLabels[m]}
              </button>
            ))}
          </div>

          <div className="f-card">
            <div className="h-[300px]">
              {loading ? (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <FaSpinner className="fa-spin text-3xl text-primary" aria-hidden />
                  <span className="text-sm font-medium text-slate-500">Loading chart…</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={mainChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill={chartMetricColors[mainChartMetric]} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="f-card overflow-hidden p-0">
            <div className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-900">
              Recent {chartMetricLabels[mainChartMetric]}
              <span className="ml-2 font-normal text-slate-500">— click a row to audit user</span>
            </div>
            <div className="overflow-x-auto">
              <table className="f-table">
                <thead>
                  <tr>
                    {mainChartMetric === 'activeUsers' && (
                      <>
                        <th>User</th>
                        <th>Platform</th>
                        <th>Activity</th>
                        <th>Last Active</th>
                      </>
                    )}
                    {mainChartMetric === 'signViews' && (
                      <>
                        <th>Sign ID</th>
                        <th>User</th>
                        <th>Platform</th>
                        <th>Time</th>
                      </>
                    )}
                    {mainChartMetric === 'pageViews' && (
                      <>
                        <th>Page</th>
                        <th>User</th>
                        <th>Platform</th>
                        <th>Time</th>
                      </>
                    )}
                    {mainChartMetric === 'searches' && (
                      <>
                        <th>Query</th>
                        <th>User</th>
                        <th>Platform</th>
                        <th>Time</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {mainChartMetric === 'activeUsers' &&
                    recentUsers.slice(0, 50).map((u) => (
                      <tr
                        key={u.userId}
                        onClick={() => openUserModal(u.userId)}
                        className={u.userId ? 'cursor-pointer hover:bg-slate-50/80' : ''}
                      >
                        <td className="font-semibold">{resolveUserName(u.userId)}</td>
                        <td>
                          {latestActivityByUser[u.userId] ? (
                            <PlatformBadge record={latestActivityByUser[u.userId]} />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {u.signViews} views • {u.pageViews} pages
                        </td>
                        <td>{timeAgo(u.lastActive)}</td>
                      </tr>
                    ))}
                  {mainChartMetric === 'signViews' &&
                    allActivity
                      .filter((d) => d.action === 'sign_view')
                      .slice(0, 50)
                      .map((sv, idx) => (
                        <tr
                          key={idx}
                          onClick={() => openUserModal(sv.userId)}
                          className={sv.userId ? 'cursor-pointer hover:bg-slate-50/80' : ''}
                        >
                          <td className="font-semibold">{sv.signId || sv.details?.signId}</td>
                          <td>{resolveUserName(sv.userId)}</td>
                          <td>
                            <PlatformBadge record={sv} />
                          </td>
                          <td>{dayjs(sv.timestamp).format('MMM D, h:mm A')}</td>
                        </tr>
                      ))}
                  {mainChartMetric === 'pageViews' &&
                    allActivity
                      .filter((d) => d.action === 'page_view')
                      .slice(0, 50)
                      .map((pv, idx) => (
                        <tr
                          key={idx}
                          onClick={() => openUserModal(pv.userId)}
                          className={pv.userId ? 'cursor-pointer hover:bg-slate-50/80' : ''}
                        >
                          <td className="font-semibold">{pv.page || pv.details?.page}</td>
                          <td>{resolveUserName(pv.userId)}</td>
                          <td>
                            <PlatformBadge record={pv} />
                          </td>
                          <td>{dayjs(pv.timestamp).format('MMM D, h:mm A')}</td>
                        </tr>
                      ))}
                  {mainChartMetric === 'searches' &&
                    allActivity
                      .filter((d) => d.action === 'search')
                      .slice(0, 50)
                      .map((s, idx) => (
                        <tr
                          key={idx}
                          onClick={() => openUserModal(s.userId)}
                          className={s.userId ? 'cursor-pointer hover:bg-slate-50/80' : ''}
                        >
                          <td className="font-semibold">{s.query || s.details?.query}</td>
                          <td>{resolveUserName(s.userId)}</td>
                          <td>
                            <PlatformBadge record={s} />
                          </td>
                          <td>{dayjs(s.timestamp).format('MMM D, h:mm A')}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
    {modalEl}
    </>
  );
};

export default Analytics;
